/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import {
  addSpotLegNote,
  removeAssignedUser,
  updateSpotLeg,
} from "../../slices/updateSpotLegSlice";
import Grid from "@material-ui/core/Grid";
import InputLabel from "@material-ui/core/InputLabel";

import CircularProgress from "@material-ui/core/CircularProgress";
import LockIcon from "@material-ui/icons/Lock";
import PublicIcon from "@material-ui/icons/Public";

import UserAutoComplete from "../common/UserAutoComplete";
import StyledTextField from "../common/StyledTextField";
import Toggle from "../common/Toggle";
import BaseButton from "../common/BaseButton";
import types from "../../helpers/types";
import {
  isAdministrator,
  isCarrier,
  isShipper,
  isShipperManager,
} from "../../helpers/goTrackUserUtils";
import { MenuItem } from "@material-ui/core";
import StyledFormControl from "../common/StyledFormControl";
import StyledSelect from "../common/StyledSelect";

function SpotLegUpdate({
  setUnsavedChanges,
  handleNotification,
  isAssigningCarrier,
}) {
  const dispatch = useDispatch();

  const user = useSelector((state) => state.goTrackUser.goTrackUser);
  const spotLegUpdate = useSelector((state) => state.updateSpotLeg);
  const spotLegDetails = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLeg
  );
  const spotLegClaim = useSelector(
    (state) => state.spotLegDetails.spotLeg.spotLegClaim
  );

  const [assignedUser, setAssignedUser] = useState({
    id: null,
    firstName: "",
    lastName: "",
  });
  const [isNotePrivate, setIsNotePrivate] = useState(false);
  const [goSpotStatus, setGoSpotStatus] = useState(spotLegDetails.status);
  const [note, setNote] = useState("");

  const isCarrierUser = isCarrier(user.roles);
  const saveRequestInProgress =
    spotLegUpdate.updateSpotLegRequestStatus ===
      types.requestStatuses.inProgress ||
    spotLegUpdate.addSpotLegNoteRequestStatus ===
      types.requestStatuses.inProgress;

  const displayPrivacyNoteToggle =
    isShipper(user.roles) ||
    isShipperManager(user.roles) ||
    isAdministrator(user.roles);

  const handleUserChange = (event, option) => {
    setAssignedUser({
      id: option?.id ?? null,
      firstName: option?.firstName ?? "",
      lastName: option?.lastName ?? "",
    });
    setUnsavedChanges(true);
  };

  const handleGoSpotStatusChange = ({ target: { value } }) => {
    setGoSpotStatus(Number(value));
    setUnsavedChanges(true);
  };

  const handlePrivacyToggle = (event) => {
    setIsNotePrivate(!isNotePrivate);
  };

  const handleNoteTextFieldChange = (event) => {
    setNote(event.target.value);
    setUnsavedChanges(true);
  };

  const handleSaveButtonClick = async () => {
    setUnsavedChanges(false);
    if (!isCarrierUser) {
      const updates = {
        spotLegsId: spotLegDetails.spotLegsId,
      };
      if (goSpotStatus !== spotLegDetails.status) {
        updates.status = goSpotStatus;
      }

      if (
        assignedUser.id &&
        assignedUser.id !== spotLegDetails.assignedUser?.id
      ) {
        updates.assignedUserId = assignedUser.id;
      }
      if (Object.keys(updates).length > 1) {
        dispatch(updateSpotLeg(updates, handleNotification));
      }

      if (assignedUser.id == null && spotLegDetails.assignedUser?.id) {
        dispatch(
          removeAssignedUser(spotLegDetails.spotLegsId, handleNotification)
        );
      }
    }

    if (note) {
      await dispatch(
        addSpotLegNote(
          spotLegDetails.spotLegsId,
          { contents: note, isPrivate: !isNotePrivate },
          handleNotification
        )
      );
      setNote("");
    }
  };

  useEffect(() => {
    setGoSpotStatus(spotLegDetails.status);
    setAssignedUser({
      id: spotLegDetails.assignedUser?.id ?? null,
      firstName: spotLegDetails.assignedUser?.firstName ?? "",
      lastName: spotLegDetails.assignedUser?.lastName ?? "",
    });
    setNote("");
  }, [spotLegDetails]);

  var disableStatusSelect =
    !!spotLegClaim ||
    goSpotStatus === types.spotLegStatusEnum.coveredAssignedCarrier ||
    goSpotStatus === types.spotLegStatusEnum.coveredBrown;
  return (
    <>
      {!isCarrierUser && (
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <StyledFormControl variant="outlined" fullWidth={true}>
              <InputLabel id="gospot-status">Leg Status</InputLabel>
              <StyledSelect
                value={goSpotStatus}
                label="Leg Status"
                labelId="gospot-status"
                onChange={handleGoSpotStatusChange}
                disabled={disableStatusSelect}
                data-testid="spotleg-update-status-update-select"
              >
                <MenuItem value={types.spotLegStatusEnum.uncovered}>
                  {types.spotLegStatus.uncovered}
                </MenuItem>
                <MenuItem value={types.spotLegStatusEnum.coveredBrown}>
                  {types.spotLegStatus.coveredBrown}
                </MenuItem>
                {spotLegDetails.status ===
                  types.spotLegStatusEnum.coveredAssignedCarrier && (
                  <MenuItem
                    value={types.spotLegStatusEnum.coveredAssignedCarrier}
                  >
                    {types.spotLegStatus.coveredAssignedCarrier}
                  </MenuItem>
                )}
                <MenuItem value={types.spotLegStatusEnum.posted}>
                  {types.spotLegStatus.posted}
                </MenuItem>
                <MenuItem value={types.spotLegStatusEnum.cancelled}>
                  {types.spotLegStatus.cancelled}
                </MenuItem>
                <MenuItem value={types.spotLegStatusEnum.claimed}>
                  {types.spotLegStatus.claimed}
                </MenuItem>
              </StyledSelect>
            </StyledFormControl>
          </Grid>
          <Grid item xs={6}>
            <UserAutoComplete
              selectedUser={assignedUser}
              handleChange={handleUserChange}
              label="Assigned To"
              width="100%"
              autocompleteTestId="spotleg-update-user-autocomplete"
            />
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid item xs={7}>
          <StyledTextField
            label="Add Note (optional)"
            variant="outlined"
            fullWidth={true}
            multiline={true}
            value={note}
            onChange={handleNoteTextFieldChange}
            inputProps={{
              maxLength: 240,
              "data-testid": "spotleg-update-note-text-field",
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <BaseButton
            onClick={handleSaveButtonClick}
            style={{ marginTop: "6px" }}
            disabled={saveRequestInProgress}
            startIcon={
              saveRequestInProgress && !isAssigningCarrier ? (
                <CircularProgress size={15} />
              ) : undefined
            }
            data-testid="spotleg-submit-button"
          >
            Save Changes
          </BaseButton>
        </Grid>
      </Grid>
      {displayPrivacyNoteToggle && (
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={7}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                Private note
              </span>
              <LockIcon style={{ marginLeft: "4px" }} fontSize="inherit" />
              <Toggle checked={isNotePrivate} onChange={handlePrivacyToggle} />
              <span style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                Public note
              </span>
              <PublicIcon style={{ marginLeft: "4px" }} fontSize="inherit" />
            </div>
          </Grid>
        </Grid>
      )}
    </>
  );
}

SpotLegUpdate.propTypes = {
  handleNotification: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
  isAssigningCarrier: PropTypes.bool,
};

export default SpotLegUpdate;
